import { useEffect } from 'react';
import { Loader2, MessageCircle, PhoneCall, X } from 'lucide-react';
import { useUpdateCallbackStatusMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { Callback, CallbackStatus } from '@/types';
import { CALLBACK_STATUSES, STATUS_BADGE, formatAge, formatPlan } from './shared';

interface CallbackDrawerProps {
    callback: Callback | null;
    onClose: () => void;
    onError: (message: string) => void;
}

/** `+2348012345678` → `2348012345678`, which is what wa.me expects. */
const waNumber = (phone: string) => phone.replace(/[^\d]/g, '');

function Row({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5 border-b border-gray-100 py-3 last:border-0 sm:flex-row sm:items-baseline sm:gap-4">
            <span className="w-32 shrink-0 text-xs font-medium tracking-wide text-gray-500 uppercase">{label}</span>
            <span className="min-w-0 text-sm break-words text-gray-900">{children}</span>
        </div>
    );
}

export default function CallbackDrawer({ callback, onClose, onError }: CallbackDrawerProps) {
    const [updateStatus, { isLoading }] = useUpdateCallbackStatusMutation();

    useEffect(() => {
        if (!callback) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        const previous = document.body.style.overflow;
        document.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previous;
        };
    }, [callback, onClose]);

    if (!callback) return null;

    const handleStatusChange = async (status: CallbackStatus) => {
        if (status === callback.status) return;
        try {
            await updateStatus({ id: callback.id, status }).unwrap();
        } catch (err) {
            onError(parseErrorMessage(err, "Couldn't update that lead. Please try again."));
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/30"
            role="dialog"
            aria-modal="true"
            aria-label={`Callback from ${callback.name}`}
            onClick={onClose}
        >
            <div
                className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl"
                onClick={(event) => event.stopPropagation()}
            >
                <header className="flex items-start justify-between gap-3 border-b border-gray-100 p-5">
                    <div className="min-w-0">
                        <h2 className="truncate text-lg font-semibold text-gray-900">{callback.name}</h2>
                        <p className="mt-0.5 text-sm text-gray-500">Requested {formatAge(callback.createdAt)}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="flex gap-2 border-b border-gray-100 p-5">
                    <a
                        href={`tel:${callback.phoneNumber}`}
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#FF6B35] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#F05A22]"
                    >
                        <PhoneCall className="h-4 w-4" aria-hidden="true" />
                        Call
                    </a>
                    <a
                        href={`https://wa.me/${waNumber(callback.phoneNumber)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    >
                        <MessageCircle className="h-4 w-4" aria-hidden="true" />
                        WhatsApp
                    </a>
                </div>

                <div className="border-b border-gray-100 p-5">
                    <label
                        htmlFor="callback-status"
                        className="mb-2 block text-xs font-medium tracking-wide text-gray-500 uppercase"
                    >
                        Status
                    </label>
                    <div className="flex items-center gap-2">
                        <select
                            id="callback-status"
                            value={callback.status}
                            disabled={isLoading}
                            onChange={(event) => handleStatusChange(event.target.value as CallbackStatus)}
                            className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm capitalize focus:border-[#FF6B35] focus:outline-none disabled:opacity-60"
                        >
                            {CALLBACK_STATUSES.map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden="true" />}
                    </div>
                </div>

                <div className="px-5 pb-6">
                    <Row label="Phone">
                        <a href={`tel:${callback.phoneNumber}`} className="text-[#FF6B35] hover:underline">
                            {callback.phoneNumber}
                        </a>
                    </Row>
                    <Row label="Email">
                        <a href={`mailto:${callback.email}`} className="text-[#FF6B35] hover:underline">
                            {callback.email}
                        </a>
                    </Row>
                    <Row label="Plan">{formatPlan(callback.planId)}</Row>
                    <Row label="Source">{callback.source ?? '—'}</Row>
                    <Row label="Status">
                        <span
                            className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${STATUS_BADGE[callback.status]}`}
                        >
                            {callback.status}
                        </span>
                    </Row>
                    <Row label="Requested">{new Date(callback.createdAt).toLocaleString()}</Row>
                    <Row label="Updated">{new Date(callback.updatedAt).toLocaleString()}</Row>
                    {/* Abuse-triage fields: useful when a lead looks like spam, but
                        noise in the queue itself, so they live here only. */}
                    <Row label="IP address">{callback.ipAddress ?? '—'}</Row>
                    <Row label="User agent">
                        <span className="text-xs text-gray-500">{callback.userAgent ?? '—'}</span>
                    </Row>
                </div>
            </div>
        </div>
    );
}
